import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { format, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import type { ActivityType } from "@/hooks/useActivityTypes";
import DateStrip from "@/components/shared/DateStrip";

interface OpenMatchFiltersProps {
  dateFilter: string;
  onDateChange: (date: string) => void;
  typeFilter: string;
  onTypeChange: (type: string) => void;
  onlyJoinable: boolean;
  onOnlyJoinableChange: (v: boolean) => void;
  matchMyLevel?: boolean;
  onMatchMyLevelChange?: (v: boolean) => void;
  hideTypeFilter?: boolean;
  hideToggles?: boolean;
  excludeMatchType?: boolean;
  activityTypes?: ActivityType[];
  organizationId?: string;
}

export default function OpenMatchFilters({
  dateFilter,
  onDateChange,
  typeFilter,
  onTypeChange,
  onlyJoinable,
  onOnlyJoinableChange,
  matchMyLevel,
  onMatchMyLevelChange,
  hideTypeFilter,
  hideToggles,
  excludeMatchType,
  activityTypes = [],
  organizationId,
}: OpenMatchFiltersProps) {
  const { t } = useTranslation("activities");
  // Convert string dateFilter to Date | null for DateStrip
  const selectedDate = useMemo(() => {
    if (!dateFilter) return null;
    const d = new Date(dateFilter + "T00:00:00");
    return isNaN(d.getTime()) ? null : startOfDay(d);
  }, [dateFilter]);

  const handleDateSelect = useCallback(
    (date: Date | null) => {
      onDateChange(date ? format(date, "yyyy-MM-dd") : "");
    },
    [onDateChange]
  );

  const typeOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: "all", label: "Alla" }];
    for (const t of activityTypes) {
      opts.push({ value: t.slug, label: t.name });
    }
    return opts;
  }, [activityTypes]);

  return (
    <div className="space-y-3">
      {/* Date strip — scrollable 30-day picker */}
      <DateStrip
        selectedDate={selectedDate}
        onSelect={handleDateSelect}
        organizationId={organizationId}
      />

      {/* Type filter — horizontal scroll badges */}
      {!hideTypeFilter && (
      <ScrollArea className="w-full">
        <div className="flex gap-1.5 pb-1">
          {typeOptions.filter(opt => !excludeMatchType || opt.value !== "match").map((opt) => (
            <Badge
              key={opt.value}
              variant={typeFilter === opt.value ? "default" : "outline"}
              className={cn(
                "cursor-pointer shrink-0 select-none transition-colors",
                typeFilter === opt.value
                  ? ""
                  : "hover:bg-muted"
              )}
              onClick={() => onTypeChange(opt.value)}
            >
              {opt.label}
            </Badge>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      )}

      {/* Toggles */}
      {!hideToggles && (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Switch checked={onlyJoinable} onCheckedChange={onOnlyJoinableChange} id="joinable" />
          <Label htmlFor="joinable" className="text-sm text-muted-foreground cursor-pointer">
            {t('onlyAvailableMatches')}
          </Label>
        </div>
        {onMatchMyLevelChange && (
          <div className="flex items-center gap-2">
            <Switch checked={matchMyLevel ?? false} onCheckedChange={onMatchMyLevelChange} id="matchLevel" />
            <Label htmlFor="matchLevel" className="text-sm text-muted-foreground cursor-pointer">
              {t('matchMyLevel')}
            </Label>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
